import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'
import { mapLanguageToComponentProps } from 'i18n'
import { getValueAndFieldByModel } from 'utils/form'
import IntlTelInput from 'react-intl-tel-input'
import 'react-intl-tel-input/dist/main.css'
import styled from 'styled-components'
import { InputStyle } from 'components/form-components/styled-text-input'
import { Small } from 'components/styled/typography'
import config from '../../config'

const propTypes = {
  i18n: PropTypes.shape({
    formErrors: PropTypes.shape({
      isValidMobilePhone: PropTypes.func.isRequired,
      duplicateNumber: PropTypes.string,
    }).isRequired,
    phoneInput: PropTypes.shape({
      placeholder: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  onChange: PropTypes.func,
  model: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  isRequired: PropTypes.bool,
  value: PropTypes.string,
  isDuplicate: PropTypes.bool,
  onBlur: PropTypes.func,
  showError: PropTypes.bool,
  useRtl: PropTypes.bool,
  excludeCountries: PropTypes.bool,
}

const defaultProps = {
  model: undefined,
  isRequired: false,
  onChange: () => {},
  onBlur: () => {},
  value: undefined,
  isDuplicate: false,
  showError: false,
  useRtl: false,
  excludeCountries: false,
}

const mapStateToProps = (state, { model }) => {
  if (model) {
    const { value } = getValueAndFieldByModel(state, model)
    return {
      value,
    }
  }
  return {}
}

const StyledPhoneInput = styled.div`
  display: flex;
  flex-flow: column;

  .intl-tel-input .country-list {
    position: relative;
    z-index: 1000;
  }

  .country {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  .iti-mobile .intl-tel-input.iti-container {
    z-index: 9999;
    overflow-y: scroll;
  }

  .iti-flag {
    margin: 0 ${({ theme }) => theme.space.s8};
  }

  .flag-box .iti-flag {
    margin: 0;
  }

  input[type='tel'] {
    ${InputStyle};
    text-align: ${({ useRtl }) => (useRtl ? 'right' : 'left')} !important;
    padding-left: ${({ useRtl }) => (useRtl ? '6px' : '52px')} !important;
    padding-right: ${({ useRtl }) => (useRtl ? '52px' : '6px')} !important;
  }

  .phone-input__error {
    font-size: ${({ theme }) => theme.space.s12};
    color: ${({ theme }) => theme.color.error110};
  }
`

export class PhoneInput extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      hasError: false,
      inputValue: '',
    }

    this.onChange = this.onChange.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.onFocus = this.onFocus.bind(this)
  }

  componentDidMount() {
    // If we don't have value when this is mount set error or user can
    // submit form without value
    const { dispatch, isRequired, model } = this.props
    const { inputValue } = this.state

    if (model && isRequired && inputValue.length === 0) {
      dispatch(actions.setErrors(model, { isRequired: true }))
    }
  }

  onChange(valid, inputValue, countryData, formattedValue) {
    const { dispatch, isRequired, onChange, model, isDuplicate } = this.props
    const isValid =
      (valid || (!isRequired && inputValue.length === 0)) && !isDuplicate
    this.setState({
      inputValue,
    })

    if (model) {
      dispatch(actions.change(model, formattedValue))
      dispatch(actions.setValidity(model, { isValidMobilePhone: isValid }))
    }
    onChange(formattedValue, isValid)
  }

  onBlur(valid, inputValue, countryData, formattedValue) {
    const { dispatch, isRequired, onBlur, model, isDuplicate } = this.props
    const isValid = valid || (!isRequired && inputValue.length === 0)
    this.setState({ hasError: !isValid })

    if (model) {
      dispatch(actions.blur(model))
      dispatch(actions.change(model, formattedValue))
      dispatch(
        actions.setValidity(model, {
          isValidMobilePhone: isValid && !isDuplicate,
        })
      )
    }
    onBlur(formattedValue, isValid)
  }

  onFocus() {
    const { model, dispatch } = this.props

    this.setState({ hasError: false })
    if (model) {
      dispatch(actions.focus(model))
    }
  }

  render() {
    const {
      i18n,
      isRequired,
      showError,
      value,
      isDuplicate,
      useRtl,
      excludeCountries,
    } = this.props
    const { inputValue, hasError } = this.state

    return (
      <StyledPhoneInput className="phone-input" useRtl={useRtl}>
        <IntlTelInput
          ariaLabel="Phone number"
          preferredCountries={[
            'fi',
            'gb',
            'de',
            'se',
            'no',
            'dk',
            'es',
            'it',
            'jp',
          ]}
          excludeCountries={
            excludeCountries
              ? Object.values(config.EXCLUDED_SMS_COUNTRY_CODES)
              : []
          }
          onPhoneNumberChange={this.onChange}
          onPhoneNumberFocus={this.onFocus}
          onPhoneNumberBlur={this.onBlur}
          customPlaceholder={example =>
            `${i18n.phoneInput.placeholder(example)}${isRequired ? '*' : ''}`
          }
          defaultValue={value || ''}
          telInputProps={{
            'aria-label': 'Phone number',
            onInput: e => {
              e.target.value = e.target.value.replace(/[^0-9.+\-()\s]/g, '')
            },
          }}
          format
        />
        {showError && hasError && (
          <Small color="error110" className="phone-input__error">
            {i18n.formErrors.isValidMobilePhone(inputValue)}
          </Small>
        )}
        {isDuplicate && (
          <Small color="error110" className="phone-input__error">
            {i18n.formErrors.duplicateNumber}
          </Small>
        )}
      </StyledPhoneInput>
    )
  }
}

PhoneInput.propTypes = propTypes
PhoneInput.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(PhoneInput)
